import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { Layout } from "@/components/Layout";
import { 
  Map, 
  Upload, 
  Radio, 
  Trash2, 
  ZoomIn, 
  ZoomOut, 
  Move,
  Circle,
  Save,
  FolderOpen,
  Plus,
  X,
  MapPin,
  Box,
  Layers,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { systemsAPI } from "@/lib/api";

// Lazy load 3D component to avoid SSR issues
const CoverageMap3D = lazy(() => import("@/components/CoverageMap3D"));

export default function CoverageMapPage({ user, onLogout }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [viewMode, setViewMode] = useState("2d"); // "2d" or "3d"
  const [floorPlan, setFloorPlan] = useState(null);
  const [floorPlanUrl, setFloorPlanUrl] = useState(null);
  const [transmitters, setTransmitters] = useState([]);
  const [systems, setSystems] = useState([]);
  const [selectedTransmitter, setSelectedTransmitter] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState("view"); // view, add, move
  const [coverageRadius, setCoverageRadius] = useState(100); // pixels
  const [showGrid, setShowGrid] = useState(true);
  
  const [newTransmitter, setNewTransmitter] = useState({
    name: "",
    system_id: "",
    power: "high",
  });

  // Load systems on mount
  useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = async () => {
    try {
      const response = await systemsAPI.getAll();
      setSystems(response.data || []);
    } catch (error) {
      // Error handled silently
    }
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const container = containerRef.current;
    
    // Set canvas size
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    
    // Clear canvas
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Apply transformations
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);
    
    // Draw floor plan if loaded
    if (floorPlan) {
      const img = new Image();
      img.src = floorPlanUrl;
      img.onload = () => {
        // Calculate scaling to fit
        const scale = Math.min(
          (canvas.width - 40) / img.width,
          (canvas.height - 40) / img.height
        );
        const x = (canvas.width / zoom - img.width * scale) / 2;
        const y = (canvas.height / zoom - img.height * scale) / 2;
        
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Draw transmitters and coverage
        drawTransmitters(ctx, x, y, scale);
      };
    } else {
      // Draw placeholder
      ctx.fillStyle = "#1A1A1A";
      ctx.fillRect(20, 20, canvas.width / zoom - 40, canvas.height / zoom - 40);
      
      ctx.fillStyle = "#71717A";
      ctx.font = "14px Inter";
      ctx.textAlign = "center";
      ctx.fillText(
        "Sube un plano de venue para comenzar",
        canvas.width / zoom / 2,
        canvas.height / zoom / 2
      );
      
      // Draw transmitters even without floor plan
      drawTransmitters(ctx, 0, 0, 1);
    }
    
    ctx.restore();
  }, [floorPlan, floorPlanUrl, transmitters, zoom, pan, selectedTransmitter, coverageRadius]);

  const drawTransmitters = (ctx, offsetX, offsetY, scale) => {
    transmitters.forEach((tx, idx) => {
      const x = tx.x;
      const y = tx.y;
      
      // Draw coverage circle
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, tx.radius || coverageRadius);
      
      // Color based on power
      let color = "#34C759"; // green for high
      if (tx.power === "medium") color = "#FFCC00";
      if (tx.power === "low") color = "#FF9500";
      
      gradient.addColorStop(0, color + "40");
      gradient.addColorStop(0.7, color + "20");
      gradient.addColorStop(1, color + "00");
      
      ctx.beginPath();
      ctx.arc(x, y, tx.radius || coverageRadius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      
      // Draw transmitter icon
      const isSelected = selectedTransmitter?.id === tx.id;
      ctx.beginPath();
      ctx.arc(x, y, isSelected ? 14 : 10, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? "#8B5CF6" : color;
      ctx.fill();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw antenna lines
      ctx.beginPath();
      ctx.moveTo(x, y - 10);
      ctx.lineTo(x, y - 20);
      ctx.moveTo(x - 5, y - 18);
      ctx.lineTo(x, y - 20);
      ctx.lineTo(x + 5, y - 18);
      ctx.strokeStyle = isSelected ? "#8B5CF6" : color;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw label
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "bold 11px Inter";
      ctx.textAlign = "center";
      ctx.fillText(tx.name, x, y + 28);
      
      // Draw frequency
      if (tx.frequency) {
        ctx.fillStyle = "#71717A";
        ctx.font = "10px JetBrains Mono";
        ctx.fillText(`${tx.frequency} MHz`, x, y + 40);
      }
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFloorPlanUrl(event.target.result);
      setFloorPlan(file);
      toast.success("Plano cargado correctamente");
    };
    reader.readAsDataURL(file);
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;
    
    if (mode === "add") {
      setNewTransmitter({ ...newTransmitter, x, y });
      setShowAddDialog(true);
      setMode("view");
    } else if (mode === "view") {
      // Check if clicked on a transmitter
      const clicked = transmitters.find(tx => {
        const dist = Math.sqrt((tx.x - x) ** 2 + (tx.y - y) ** 2);
        return dist < 20;
      });
      setSelectedTransmitter(clicked || null);
    }
  };

  const handleMouseDown = (e) => {
    if (mode === "move" || e.button === 1) { // Middle click or move mode
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleAddTransmitter = () => {
    if (!newTransmitter.name.trim()) {
      toast.error("Ingresa un nombre para el transmisor");
      return;
    }
    
    const system = systems.find(s => s.id === newTransmitter.system_id);
    
    const tx = {
      id: Date.now().toString(),
      name: newTransmitter.name,
      x: newTransmitter.x,
      y: newTransmitter.y,
      power: newTransmitter.power,
      radius: newTransmitter.power === "high" ? 150 : newTransmitter.power === "medium" ? 100 : 60,
      system_id: newTransmitter.system_id,
      frequency: system?.frequency || null,
    };
    
    setTransmitters([...transmitters, tx]);
    setShowAddDialog(false);
    setNewTransmitter({ name: "", system_id: "", power: "high" });
    toast.success("Transmisor agregado");
  };

  const handleDeleteTransmitter = () => {
    if (!selectedTransmitter) return;
    setTransmitters(transmitters.filter(tx => tx.id !== selectedTransmitter.id));
    setSelectedTransmitter(null);
    toast.success("Transmisor eliminado");
  };

  const handleUpdateRadius = (value) => {
    if (!selectedTransmitter) return;
    setTransmitters(transmitters.map(tx => 
      tx.id === selectedTransmitter.id 
        ? { ...tx, radius: value[0] }
        : tx
    ));
    setSelectedTransmitter({ ...selectedTransmitter, radius: value[0] });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Mapa de Cobertura RF
          </h1>
          <p className="text-sm text-[#71717A]">
            Visualiza la cobertura de tus transmisores en el venue
          </p>
        </div>
        <div className="flex gap-2">
          {/* 2D/3D Toggle */}
          <div className="flex bg-[#1A1A1A] border border-white/10 rounded-sm overflow-hidden">
            <button
              onClick={() => setViewMode("2d")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === "2d" 
                  ? "bg-[#34C759] text-white" 
                  : "text-[#71717A] hover:text-white"
              }`}
              data-testid="view-2d-btn"
            >
              <Layers className="w-4 h-4" />
              2D
            </button>
            <button
              onClick={() => setViewMode("3d")}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === "3d" 
                  ? "bg-[#8B5CF6] text-white" 
                  : "text-[#71717A] hover:text-white"
              }`}
              data-testid="view-3d-btn"
            >
              <Box className="w-4 h-4" />
              3D
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="btn-tactical btn-tactical-secondary"
            data-testid="upload-floorplan-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Cargar Plano
          </Button>
          <Button
            onClick={() => setMode(mode === "add" ? "view" : "add")}
            className={`btn-tactical ${mode === "add" ? 'bg-[#34C759] hover:bg-[#34C759]/90' : 'btn-tactical-primary'}`}
            data-testid="add-transmitter-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {mode === "add" ? "Cancelar" : "Agregar TX"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map Canvas / 3D View */}
        <div className="lg:col-span-3">
          {viewMode === "3d" ? (
            /* 3D View */
            <div 
              className="relative bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden"
              style={{ height: "calc(100vh - 220px)", minHeight: "500px" }}
            >
              <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center text-[#71717A]">
                  <div className="text-center">
                    <Box className="w-12 h-12 mx-auto mb-3 animate-pulse" />
                    <p>Cargando vista 3D...</p>
                  </div>
                </div>
              }>
                <CoverageMap3D
                  transmitters={transmitters}
                  floorPlanUrl={floorPlanUrl}
                  selectedTransmitter={selectedTransmitter}
                  onSelectTransmitter={setSelectedTransmitter}
                  showGrid={showGrid}
                  onAddTransmitter={(pos) => {
                    setNewTransmitter({ ...newTransmitter, x: pos.x3d * 40 + 400, y: pos.z3d * 40 + 300, x3d: pos.x3d, z3d: pos.z3d });
                    setShowAddDialog(true);
                  }}
                  style={{ height: "100%" }}
                />
              </Suspense>
              
              {/* 3D Controls */}
              <div className="absolute top-4 right-4 z-10 flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowGrid(!showGrid)}
                  className={`bg-[#1A1A1A] border border-white/10 ${showGrid ? "text-[#34C759]" : ""}`}
                  title="Mostrar/Ocultar cuadrícula"
                >
                  <Layers className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            /* 2D View */
            <div 
              ref={containerRef}
              className="relative bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden"
              style={{ height: "calc(100vh - 220px)", minHeight: "500px" }}
            >
            {/* Mode indicator */}
            {mode === "add" && (
              <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-[#34C759] text-white text-sm rounded-sm">
                Haz clic en el mapa para agregar transmisor
              </div>
            )}
            
            {/* Zoom controls */}
            <div className="absolute top-4 right-4 z-10 flex flex-col gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setZoom(Math.min(zoom + 0.2, 3))}
                className="bg-[#1A1A1A] border border-white/10 hover:bg-white/10"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setZoom(Math.max(zoom - 0.2, 0.5))}
                className="bg-[#1A1A1A] border border-white/10 hover:bg-white/10"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMode(mode === "move" ? "view" : "move")}
                className={`bg-[#1A1A1A] border border-white/10 ${mode === "move" ? "text-[#34C759]" : ""}`}
              >
                <Move className="w-4 h-4" />
              </Button>
            </div>
            
            <canvas
              ref={canvasRef}
              className={`w-full h-full ${
                mode === "add" ? "cursor-crosshair" : 
                mode === "move" ? "cursor-grab" : "cursor-default"
              }`}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </div>
          )}
        </div>

        {/* Controls Panel */}
        <div className="lg:col-span-1 space-y-4">
          {/* Transmitters List */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
            <h3 className="font-heading font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
              <Radio className="w-4 h-4 text-[#34C759]" />
              Transmisores ({transmitters.length})
            </h3>
            
            {transmitters.length === 0 ? (
              <div className="text-center py-6">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-[#71717A] opacity-30" />
                <p className="text-[#71717A] text-sm">Sin transmisores</p>
                <p className="text-[#52525B] text-xs mt-1">
                  Haz clic en "Agregar TX" y luego en el mapa
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {transmitters.map(tx => (
                  <div
                    key={tx.id}
                    onClick={() => setSelectedTransmitter(tx)}
                    className={`p-3 rounded-sm border cursor-pointer transition-colors ${
                      selectedTransmitter?.id === tx.id
                        ? "border-[#8B5CF6] bg-[#8B5CF6]/10"
                        : "border-white/[0.08] hover:border-white/20 bg-[#0A0A0A]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ 
                          backgroundColor: tx.power === "high" ? "#34C759" : 
                            tx.power === "medium" ? "#FFCC00" : "#FF9500"
                        }}
                      />
                      <span className="text-sm text-white font-medium">{tx.name}</span>
                    </div>
                    {tx.frequency && (
                      <p className="text-xs text-[#71717A] font-mono ml-5 mt-1">
                        {tx.frequency} MHz
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Transmitter Controls */}
          {selectedTransmitter && (
            <div className="bg-[#111111] border border-[#8B5CF6]/30 rounded-sm p-4">
              <h3 className="font-heading font-semibold text-[#F4F4F5] mb-4">
                {selectedTransmitter.name}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Radio de cobertura</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Slider
                      value={[selectedTransmitter.radius || coverageRadius]}
                      min={30}
                      max={200}
                      step={10}
                      onValueChange={handleUpdateRadius}
                      className="flex-1"
                    />
                    <span className="text-xs text-[#71717A] w-12 text-right">
                      {selectedTransmitter.radius || coverageRadius}px
                    </span>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  onClick={handleDeleteTransmitter}
                  className="w-full text-[#FF3B30] hover:bg-[#FF3B30]/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Transmisor
                </Button>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
            <h3 className="font-heading font-semibold text-[#F4F4F5] mb-3 text-sm">
              Leyenda
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#34C759]" />
                <span className="text-xs text-[#A1A1AA]">Alta potencia</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#FFCC00]" />
                <span className="text-xs text-[#A1A1AA]">Media potencia</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#FF9500]" />
                <span className="text-xs text-[#A1A1AA]">Baja potencia</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Transmitter Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5] flex items-center gap-2">
              <Radio className="w-5 h-5 text-[#34C759]" />
              Agregar Transmisor
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Posición: ({Math.round(newTransmitter.x || 0)}, {Math.round(newTransmitter.y || 0)})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Nombre del transmisor</Label>
              <Input
                value={newTransmitter.name}
                onChange={(e) => setNewTransmitter({...newTransmitter, name: e.target.value})}
                placeholder="Ej: TX Escenario"
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-[#A1A1AA]">Vincular a sistema (opcional)</Label>
              <Select 
                value={newTransmitter.system_id || "none"} 
                onValueChange={(v) => setNewTransmitter({...newTransmitter, system_id: v === "none" ? "" : v})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue placeholder="Seleccionar sistema" />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-white/10">
                  <SelectItem value="none" className="text-[#71717A]">Ninguno</SelectItem>
                  {systems.map(sys => (
                    <SelectItem key={sys.id} value={sys.id} className="text-white">
                      {sys.name} ({sys.frequency} MHz)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-[#A1A1AA]">Nivel de potencia</Label>
              <Select 
                value={newTransmitter.power} 
                onValueChange={(v) => setNewTransmitter({...newTransmitter, power: v})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-white/10">
                  <SelectItem value="high" className="text-white">Alta (30mW)</SelectItem>
                  <SelectItem value="medium" className="text-white">Media (10mW)</SelectItem>
                  <SelectItem value="low" className="text-white">Baja (5mW)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowAddDialog(false)}
                className="text-[#A1A1AA] hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddTransmitter}
                className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
